/**
 * Employee-id input that confirms who the id belongs to before submitting.
 *
 * This is the field the whole feature turns on: for a marketing employee or
 * manager the id is what limits the account to their own hierarchy, so getting
 * it wrong hands one person another's numbers. Typing an id and finding out on
 * submit — or worse, three weeks later when a dashboard looks odd — is not good
 * enough, so the id is resolved as it is typed and the matched person is shown.
 *
 * Two audiences, one component:
 *
 * * On the public registration form the name comes back **masked**
 *   (`R••••••• Y•••••`). A real employee recognises themselves; someone walking
 *   the sequential id space learns almost nothing.
 * * For an administrator it comes back in full, with the designation and the
 *   person's other ids — which is what makes it obvious that MR-EMP-0042 and
 *   WL-NXP-70615 are one human rather than two.
 */

import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Search } from "lucide-react";

import { api } from "@/api/client";
import type { EmployeeIdCheck } from "@/types/api";

/** Long enough that a manager typing `MR-EMP-0042` is not looked up nine times. */
const DEBOUNCE_MS = 450;

export interface EmployeeIdFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Resolution is lifted so the parent can block submit on a bad id. */
  onResolved?: (check: EmployeeIdCheck | null) => void;
  /** Admins hit the authenticated endpoint and see the unmasked name. */
  admin?: boolean;
  required?: boolean;
  errors?: string[];
  disabled?: boolean;
  label?: string;
  hint?: string;
}

export function EmployeeIdField({
  value, onChange, onResolved, admin = false, required = false,
  errors = [], disabled = false, label = "Employee ID", hint,
}: EmployeeIdFieldProps) {
  const [check, setCheck] = useState<EmployeeIdCheck | null>(null);
  const [busy, setBusy] = useState(false);
  // Guards against a slow early request landing after a fast later one and
  // showing the wrong person's name against the current input.
  const sequence = useRef(0);

  useEffect(() => {
    const typed = value.trim();
    if (!typed) {
      setCheck(null);
      onResolved?.(null);
      return;
    }
    const mine = ++sequence.current;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post<EmployeeIdCheck>(
          admin ? "/admin/employee-id" : "/auth/check-employee-id",
          { employee_id: typed });
        if (mine !== sequence.current) return;
        setCheck(data);
        onResolved?.(data);
      } catch (err) {
        if (mine !== sequence.current) return;
        // A 429 here is the rate limit on the public lookup, not a bad id.
        const status = (err as { response?: { status?: number } })?.response?.status;
        const failure: EmployeeIdCheck = {
          ok: false, masked: !admin, name: "", namespace: "",
          reason: status === 429
            ? "Too many checks from this address. Wait a few minutes, or ask an administrator to create the account."
            : "Could not check that id just now. You can still submit — it is checked again on the server.",
        };
        setCheck(failure);
        onResolved?.(failure);
      } finally {
        if (mine === sequence.current) setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // onResolved is intentionally excluded: parents pass an inline closure and
    // including it would re-run the lookup on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, admin]);

  const invalid = errors.length > 0 || (check !== null && !check.ok && !busy);

  return (
    <div className="space-y-1.5">
      <label className="block space-y-1">
        <span className="label">
          {label}{required && <span className="text-bad"> *</span>}
        </span>
        <span className="relative block">
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="MR-EMP-0042"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={invalid}
            className={`w-full text-sm font-mono bg-surface border rounded-md pl-2.5 pr-9 py-2
                        focus:outline-none focus:ring-2 focus:ring-petrol/30
                        disabled:opacity-60 ${invalid ? "border-bad" : "border-rule"}`}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {busy
              ? <Loader2 size={14} className="animate-spin text-ink-faint" />
              : check?.ok
                ? <CheckCircle2 size={14} className="text-good" />
                : check
                  ? <AlertCircle size={14} className="text-bad" />
                  : <Search size={14} className="text-ink-faint" />}
          </span>
        </span>
      </label>

      {hint && !check && !errors.length && (
        <p className="text-[11.5px] text-ink-faint">{hint}</p>
      )}

      {/* Server-side errors win: they are the authoritative answer. */}
      {errors.map((message) => (
        <p key={message} className="text-[12px] text-bad">{message}</p>
      ))}

      {!errors.length && check && !busy && (
        check.ok ? <Matched check={check} /> : (
          <p className="text-[12px] text-bad flex items-start gap-1.5">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>{check.reason}</span>
          </p>
        )
      )}
    </div>
  );
}

function Matched({ check }: { check: EmployeeIdCheck }) {
  return (
    <div className="rounded-md border border-good/30 bg-good-soft px-2.5 py-2 space-y-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <CheckCircle2 size={12} className="text-good translate-y-0.5" />
        <span className="text-[13px] font-medium">{check.name || "matched"}</span>
        {check.designation && (
          <span className="text-[11.5px] text-ink-muted">{check.designation}</span>
        )}
        {check.namespace && (
          <span className="chip border-transparent px-1 py-0 text-[10.5px]
                           bg-surface text-ink-muted">
            {check.namespace}
          </span>
        )}
      </div>
      {check.masked && (
        <p className="text-[11px] text-ink-faint">
          Name is partly hidden on purpose. If those initials are not yours, the
          id is wrong.
        </p>
      )}
      {!!check.also_known_as?.length && (
        <p className="text-[11px] text-ink-faint">
          Same person also recorded as{" "}
          <span className="font-mono">{check.also_known_as.join(", ")}</span> —
          the source issues a new id on every transfer.
        </p>
      )}
    </div>
  );
}
